/**
 * @file time-picker/use-time-picker-state.ts
 *
 * Core state hook for TimePicker — hoisted state pattern.
 * Ported from `rememberTimePickerState()` in Android Compose Material3.
 *
 * Separates `hour`/`minute` (always valid) and `hourInput`/`minuteInput`
 * (raw input, can be invalid mid-typing) — preserving Kotlin semantics.
 *
 * @see docs/m3/date-and-time-pickers/time-pickers/TimePickerState.md
 *
 * @example
 * ```tsx
 * const state = useTimePickerState({
 *   initialHour: 7,
 *   initialMinute: 0,
 *   is24hour: false,
 * });
 *
 * <TimePicker state={state} />
 * <p>{state.hour}:{String(state.minute).padStart(2, '0')} {state.isPm ? 'PM' : 'AM'}</p>
 * ```
 */

import { useCallback, useMemo, useState } from "react";
import type {
	TimePickerSelectionMode,
	TimePickerState,
	UseTimePickerStateOptions,
} from "./time-picker.types";

// ─── Helpers ───────────────────────────────────────────────────────────────────

/** Clamps a number within [min, max] range. */
const clamp = (value: number, min: number, max: number): number =>
	Math.max(min, Math.min(max, value));

/**
 * Validates hour input according to time format.
 * 24h: 0-23. 12h: 1-12 (user-facing 12h clock).
 */
const isValidHourInput = (h: number, is24hour: boolean): boolean =>
	is24hour ? h >= 0 && h <= 23 : h >= 1 && h <= 12;

/** Validates minute input: 0-59 */
const isValidMinuteInput = (m: number): boolean => m >= 0 && m <= 59;

/**
 * Converts 12h input + isPm → 24h internal value.
 * Standard mapping:
 *   12 AM = 0, 1 AM = 1, ..., 11 AM = 11
 *   12 PM = 12, 1 PM = 13, ..., 11 PM = 23
 */
const to24Hour = (hour12: number, isPm: boolean): number => {
	if (isPm) {
		return hour12 === 12 ? 12 : hour12 + 12;
	}
	return hour12 === 12 ? 0 : hour12;
};

/**
 * Convert 24h internal → 12h display value.
 * 0 → 12 AM, 13 → 1 PM, 12 → 12 PM
 */
const to12Hour = (hour24: number): number => {
	if (hour24 === 0) return 12;
	if (hour24 > 12) return hour24 - 12;
	return hour24;
};

// ─── Hook ──────────────────────────────────────────────────────────────────────

/**
 * State management hook for TimePicker and TimeInput.
 * Equivalent to `rememberTimePickerState()` in Android Compose.
 *
 * @param options - Initial configuration options
 * @returns `TimePickerState` object with state and actions
 */
export const useTimePickerState = (
	options?: UseTimePickerStateOptions,
): TimePickerState => {
	// ── Initial values ────────────────────────────────────────────────────────
	const initialHour = clamp(options?.initialHour ?? 0, 0, 23);
	const initialMinute = clamp(options?.initialMinute ?? 0, 0, 59);
	const is24hour = options?.is24hour ?? false;

	// ── State ─────────────────────────────────────────────────────────────────
	const [hour, setHourState] = useState<number>(initialHour);
	const [minute, setMinuteState] = useState<number>(initialMinute);
	// hourInput: raw display value (12h when !is24hour)
	const [hourInput, setHourInputState] = useState<number>(
		is24hour ? initialHour : to12Hour(initialHour),
	);
	const [minuteInput, setMinuteInputState] = useState<number>(initialMinute);
	const [selection, setSelectionState] =
		useState<TimePickerSelectionMode>("hour");

	// ── Computed ──────────────────────────────────────────────────────────────
	const isPm = hour >= 12;
	const isHourInputValid = isValidHourInput(hourInput, is24hour);
	const isMinuteInputValid = isValidMinuteInput(minuteInput);
	const isInputValid = isHourInputValid && isMinuteInputValid;

	// ── Actions ───────────────────────────────────────────────────────────────

	/**
	 * Sets valid hour (0-23) — used for clock dial.
	 * Automatically updates both hour and hourInput.
	 */
	const setHour = useCallback(
		(h: number): void => {
			const clamped = clamp(h, 0, 23);
			setHourState(clamped);
			// hourInput always displays 12h format when !is24hour
			setHourInputState(is24hour ? clamped : to12Hour(clamped));
		},
		[is24hour],
	);

	/**
	 * Sets valid minute (0-59) — used for clock dial.
	 * Automatically updates both minute and minuteInput.
	 */
	const setMinute = useCallback((m: number): void => {
		const clamped = clamp(m, 0, 59);
		setMinuteState(clamped);
		setMinuteInputState(clamped);
	}, []);

	/**
	 * Sets raw hour input — used for TimeInput text field.
	 * Only updates internal `hour` (24h) when value is valid.
	 * `hourInput` is always updated for immediate UI reflection.
	 */
	const setHourInput = useCallback(
		(h: number): void => {
			setHourInputState(h);
			if (isValidHourInput(h, is24hour)) {
				// Convert to 24h internal
				const h24 = is24hour ? h : to24Hour(h, isPm);
				setHourState(clamp(h24, 0, 23));
			}
		},
		[is24hour, isPm],
	);

	/**
	 * Sets raw minute input — used for TimeInput text field.
	 * Only updates `minute` when value is valid.
	 */
	const setMinuteInput = useCallback((m: number): void => {
		setMinuteInputState(m);
		if (isValidMinuteInput(m)) {
			setMinuteState(m);
		}
	}, []);

	/** Switches selection mode between 'hour' and 'minute'. */
	const setSelection = useCallback((mode: TimePickerSelectionMode): void => {
		setSelectionState(mode);
	}, []);

	/**
	 * Toggles AM/PM.
	 * Automatically converts internal hour (24h) and updates hourInput (12h display).
	 */
	const setIsPm = useCallback((pm: boolean): void => {
		setHourState((prev) => {
			const wasAlreadyPm = prev >= 12;
			if (pm === wasAlreadyPm) return prev;
			const newHour = pm ? prev + 12 : prev - 12;
			const clamped = clamp(newHour, 0, 23);
			// Sync hourInput (12h display remains unchanged, only internal hour changes)
			setHourInputState(to12Hour(clamped));
			return clamped;
		});
	}, []);

	/**
	 * Resets to initial state.
	 */
	const reset = useCallback((): void => {
		setHourState(initialHour);
		setMinuteState(initialMinute);
		setHourInputState(is24hour ? initialHour : to12Hour(initialHour));
		setMinuteInputState(initialMinute);
		setSelectionState("hour");
	}, [initialHour, initialMinute, is24hour]);

	// ── Memoized state object ──────────────────────────────────────────────────
	return useMemo<TimePickerState>(
		() => ({
			hour,
			minute,
			hourInput,
			minuteInput,
			is24hour,
			selection,
			isInputValid,
			isHourInputValid,
			isMinuteInputValid,
			isPm,
			setHour,
			setMinute,
			setHourInput,
			setMinuteInput,
			setSelection,
			setIsPm,
			reset,
		}),
		[
			hour,
			minute,
			hourInput,
			minuteInput,
			is24hour,
			selection,
			isInputValid,
			isHourInputValid,
			isMinuteInputValid,
			isPm,
			setHour,
			setMinute,
			setHourInput,
			setMinuteInput,
			setSelection,
			setIsPm,
			reset,
		],
	);
};
